/**
 * customer-bootstrap · preflight（FOUC 防闪）
 *
 * 必须在 <body> 渲染前同步执行，因此独立成零副作用模块：
 *   - index.ts 与 preflight-entry.ts 都从这里导入
 *   - 不引入 service.ts（保持 inline 体积 < 2 KB）
 *
 * **行为契约**：与 `js/bootstrap-preflight.js`（由 preflight-entry 打包）首屏同步执行效果 1:1：
 *   1) APP_VERSION + iframe 检测 (data-embed-preview) + osPreview 自定义宽度 (?w=)
 *   2) 主题预设：localStorage.restaurantCustomerTheme + prefers-color-scheme 回落
 *   3) 背景预设：白名单 + legacy 映射 + 默认 'black'，**总是写 attribute**
 */

import {
  APP_VERSION,
  BG_PRESET_ALLOWED,
  BG_PRESET_DEFAULT,
  BG_PRESET_LEGACY,
  OS_PREVIEW_WIDTH_DEFAULT,
  OS_PREVIEW_WIDTH_MAX,
  OS_PREVIEW_WIDTH_MIN,
  STORAGE_KEYS,
  type BgPresetName,
  type PreflightResult,
  type ThemePreference,
} from './types';
import { prefersDark, readSystemSettings } from './utils';

const isInIframe = (): boolean => {
  try {
    return window.self !== window.top;
  } catch {
    /* 同源策略可能抛错，按"在 iframe 中"保守处理 */
    return true;
  }
};

/** 解析 ?osPreview=1&w=393 的宽度（边界裁剪） */
const resolveOsPreviewWidth = (): number => {
  let raw: string | null = null;
  try {
    raw = new URLSearchParams(window.location.search).get('w');
  } catch {
    /* noop */
  }
  const w = parseInt(String(raw ?? ''), 10);
  if (!Number.isFinite(w) || w < OS_PREVIEW_WIDTH_MIN || w > OS_PREVIEW_WIDTH_MAX) {
    return OS_PREVIEW_WIDTH_DEFAULT;
  }
  return w;
};

const isOsPreviewActive = (): boolean => {
  try {
    return new URLSearchParams(window.location.search).get('osPreview') === '1';
  } catch {
    return false;
  }
};

const isAllowedBgPreset = (c: string): c is BgPresetName =>
  Object.prototype.hasOwnProperty.call(BG_PRESET_ALLOWED, c);

/** 归一化背景预设：legacy 名 → 现名；非法值 → 默认 */
const normalizeBgPreset = (raw: unknown): BgPresetName => {
  const c = raw == null ? '' : String(raw).trim();
  if (isAllowedBgPreset(c)) return c;
  const legacy = BG_PRESET_LEGACY[c];
  if (legacy) return legacy;
  return BG_PRESET_DEFAULT;
};

/** 主题决策：与原 inline L52-55 完全一致的优先级 */
const resolveTheme = (): { theme: ThemePreference; dark: boolean } => {
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(STORAGE_KEYS.customerTheme);
  } catch {
    /* noop */
  }
  if (stored === 'dark') return { theme: 'dark', dark: true };
  if (stored === 'light') return { theme: 'light', dark: false };
  return { theme: 'system', dark: prefersDark() };
};

export const applyPreflight = (): PreflightResult => {
  try {
    window.APP_VERSION = APP_VERSION;
  } catch {
    /* noop */
  }

  const isEmbedded = isInIframe();
  if (isEmbedded) {
    try {
      document.documentElement.setAttribute('data-embed-preview', '1');
    } catch {
      /* noop */
    }
  }

  const isOsPreview = isOsPreviewActive();
  let osPreviewWidth = 0;
  if (isOsPreview) {
    osPreviewWidth = resolveOsPreviewWidth();
    try {
      const meta = document.querySelector('meta[name="viewport"]');
      if (meta) {
        meta.setAttribute(
          'content',
          `width=${osPreviewWidth}, initial-scale=1, viewport-fit=cover`,
        );
      }
    } catch {
      /* noop */
    }
  }

  const { theme, dark } = resolveTheme();
  try {
    document.documentElement.classList.toggle('dark', dark);
  } catch {
    /* noop */
  }

  const bgPreset = normalizeBgPreset(readSystemSettings()?.customerBackgroundPreset);
  try {
    document.documentElement.setAttribute('data-customer-bg-preset', bgPreset);
  } catch {
    /* noop */
  }

  return { theme, bgPreset, isOsPreview, isEmbedded, dark, osPreviewWidth };
};

/** 对外名称（与 `applyPreflight` 同一实现）；preflight-entry 与 window 暴露均用此名 */
export const initPreflight = applyPreflight;
